import { legacyToursSuppressed, watchLegacyTourCancellation } from './legacyTourOwnership';
import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getTourDefinition } from './tourDefinitions';
import { dispatchTaskCompleted } from './dispatchTaskCompleted';
import { markTourSeen } from './tourSeen';
import { trackOnboardingEvent } from '../analytics/onboardingEvents';
import type { TaskKey } from '../hooks/useOnboarding';

/**
 * Hook globale che intercetta `?tour=<taskId>` dalla URL e lancia il tour
 * driver.js corrispondente. Monta una sola istanza nel Layout per ascolto
 * cross-pagina.
 *
 * Comportamento:
 * 1. Quando la URL ha `?tour=<taskId>`, attende il next paint (per dare tempo
 *    alla pagina target di renderizzare i nodi con data-tour=...) e lancia.
 * 2. Se i selettori non sono presenti dopo un breve retry, abbandona
 *    silenziosamente (no crash, no popup vuoto).
 * 3. Alla chiusura/finish del tour, rimuove il query param dalla URL e marca
 *    il task come completato se la definition lo prevede.
 */
export function useDriverTour(enabled = true): void {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const driverRef = useRef<Driver | null>(null);
  const lastTourIdRef = useRef<string | null>(null);

  useEffect(() => {
    if(!enabled || legacyToursSuppressed())return;
    const tourId = searchParams.get('tour');
    if (!tourId) return;
    if (lastTourIdRef.current === tourId) return;

    const definition = getTourDefinition(tourId);
    if (!definition || definition.steps.length === 0) {
      // Tour non definito → pulisci il query param e abbandona
      lastTourIdRef.current = tourId;
      const next = new URLSearchParams(searchParams);
      next.delete('tour');
      setSearchParams(next, { replace: true });
      return;
    }

    // Polling breve: aspetta fino a 1.5s che il primo step sia montato
    let attempts = 0;
    const maxAttempts = 15;
    const interval = window.setInterval(() => {
      if(legacyToursSuppressed()){window.clearInterval(interval);return;}
      attempts += 1;
      const firstSelector = typeof definition.steps[0].element === 'string'
        ? definition.steps[0].element
        : null;
      const firstNode = firstSelector ? document.querySelector(firstSelector) : null;
      if (firstNode) {
        window.clearInterval(interval);
        lastTourIdRef.current = tourId;

        // Escludi gli step il cui elemento (selettore stringa) non è montato:
        // driver.js altrimenti mostrerebbe un popover fluttuante senza highlight
        // per gli step intermedi assenti (es. area stage pipeline non espansa,
        // sezioni vuote su account nuovo). Gli step senza selettore stringa
        // (element funzione/assente = popover centrato) restano.
        const liveSteps = definition.steps.filter((s) =>
          typeof s.element !== 'string' || document.querySelector(s.element)
        );
        if (liveSteps.length === 0) {
          const next = new URLSearchParams(window.location.search);
          next.delete('tour');
          setSearchParams(next, { replace: true });
          return;
        }

        // Mutua esclusione coi tour inline (stessa guardia di launchInlineTour): driver.js
        // 1.4 ha config singleton di modulo, un secondo driver() clobbererebbe onDestroyed
        // del tour attivo. Non si rimuove il query param: si ritenta alla prossima nav.
        if (document.body.classList.contains('driver-active')) {
          lastTourIdRef.current = null;
          return;
        }

        let interrupted=false;
        let stopWatching=()=>{};
        const d = driver({
          showProgress: liveSteps.length > 1,
          allowClose: true,
          overlayColor: 'rgba(15, 23, 42, 0.55)',
          stagePadding: 4,
          stageRadius: 8,
          smoothScroll: true,
          nextBtnText: 'Avanti',
          prevBtnText: 'Indietro',
          doneBtnText: 'Fatto',
          progressText: '{{current}} di {{total}}',
          steps: liveSteps,
          onDestroyed: () => {
            stopWatching();
            if(interrupted){driverRef.current=null;lastTourIdRef.current=null;return;}
            // Il tour è stato effettivamente mostrato e chiuso/completato:
            // SOLO ora marca il flag permanente "visto" (vedi tourSeen).
            markTourSeen(tourId);
            // Pulisci query param
            const next = new URLSearchParams(window.location.search);
            next.delete('tour');
            setSearchParams(next, { replace: true });
            // Track + optionally complete task
            trackOnboardingEvent('flusia.onboarding.checklist_task_completed', {
              task: tourId,
              via: 'tour_finished',
            });
            if (definition.completeOnFinish) {
              dispatchTaskCompleted(tourId as TaskKey, 'tour_finished');
            }
          },
        });

        driverRef.current = d;
        stopWatching=watchLegacyTourCancellation(()=>{interrupted=true;d.destroy();});
        if(interrupted)return;
        d.drive();
        trackOnboardingEvent('flusia.onboarding.step_viewed', {
          step: tourId,
          via: 'driver_tour',
        });
      } else if (attempts >= maxAttempts) {
        // Target mai apparso → silenzioso, ripulisci query param
        window.clearInterval(interval);
        lastTourIdRef.current = tourId;
        const next = new URLSearchParams(searchParams);
        next.delete('tour');
        setSearchParams(next, { replace: true });
      }
    }, 100);

    return () => {
      window.clearInterval(interval);
    };
    // Riavvia quando cambia la URL (utile se l'utente naviga in pagine diverse
    // mantenendo la query param).
  }, [enabled, searchParams, location.pathname, setSearchParams]);
}
